import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { Html5Qrcode, CameraDevice } from "html5-qrcode";
import api from "../services/api";
import { useConsent } from "../context/ConsentContext";
import { QrCode, ApiResponse, CategoryType, StatusType } from "../types";
import { formatDate, formatDateTime, getCategoryBadge, getStatusBadge } from "../utils/utils";

type LookupStatus = 'idle' | 'loading' | 'found' | 'notfound' | 'error';

interface LookupResult {
    status: LookupStatus;
    message: string;
    qrCode?: QrCode;
}

const Icons = {
    Camera: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>,
    Search: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/></svg>,
    Info: <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    NotFound: <svg className="w-16 h-16" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>,
    Refresh: <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" /></svg>,
};

const ConsentBlockedView: FC = () => (
    <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center">
        <div className="max-w-md p-8 bg-slate-800 border border-slate-700 rounded-2xl shadow-2xl">
            <h2 className="text-2xl font-bold text-white mb-4">Funzionalità Bloccata</h2>
            <p className="text-slate-400 mb-6">
                Per utilizzare lo scanner, è necessario accettare i termini della nostra Privacy Policy.
            </p>
        </div>
    </div>
);

const InfoRow: FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
    <div className="flex justify-between items-center gap-3 py-2 border-b border-slate-700/50 last:border-b-0">
        <span className="text-slate-400 text-sm">{label}</span>
        <span className="text-white text-sm font-medium text-right break-all">{children}</span>
    </div>
);

const QrInfoCard: FC<{ qr: QrCode }> = ({ qr }) => (
    <div className="bg-slate-800/40 backdrop-blur-md border border-slate-700/50 rounded-2xl p-6 shadow-xl animate-fade-in">
        <div className="flex justify-between items-start mb-4 gap-3">
            <div className="min-w-0">
                <p className="text-slate-400 text-xs uppercase tracking-wider mb-1">QR Code</p>
                <h2 className="text-2xl font-bold text-white truncate">{qr.name}</h2>
            </div>
            <div className="flex flex-col items-end gap-2 flex-shrink-0">
                {getStatusBadge(qr.status as StatusType)}
                {qr.os && (
                    <span className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider rounded-md bg-amber-500/20 text-amber-300 border border-amber-500/40">
                        One Shot
                    </span>
                )}
            </div>
        </div>

        <div className="space-y-0">
            <InfoRow label="ID">{qr.id}</InfoRow>
            <InfoRow label="Codice">
                <span className="font-mono text-xs">{qr.code}</span>
            </InfoRow>
            <InfoRow label="Categoria">{getCategoryBadge(qr.category as CategoryType)}</InfoRow>
            <InfoRow label="Cliente">{qr.client || 'Nessuno'}</InfoRow>
            {qr.os && qr.oneshotName && (
                <InfoRow label="Gruppo One Shot">{qr.oneshotName}</InfoRow>
            )}
            <InfoRow label="Valido dal">
                {qr.validFrom ? formatDate(qr.validFrom) : 'Non specificato'}
            </InfoRow>
            <InfoRow label="Valido al">
                {qr.validTo ? formatDate(qr.validTo) : 'Non specificato'}
            </InfoRow>
            <InfoRow label="Max usi totali">
                {qr.maxUses === -1 ? 'Illimitati' : qr.maxUses}
            </InfoRow>
            <InfoRow label="Max usi per fascia">
                {qr.maxUsesFasce === -1 ? 'Illimitati' : qr.maxUsesFasce}
            </InfoRow>
            <InfoRow label="Scansioni effettuate">{qr.scanCount}</InfoRow>
            <InfoRow label="Fasce orarie">
                {qr.availability && qr.availability.length > 0
                    ? qr.availability.map(s => `${s.from} - ${s.to}`).join(', ')
                    : 'Non specificato'}
            </InfoRow>
            <InfoRow label="Creato il">
                {qr.created ? formatDateTime(qr.created) : '-'}
            </InfoRow>
            {qr.note && <InfoRow label="Note">{qr.note}</InfoRow>}
        </div>
    </div>
);

const QrInfoLookupPage: FC = () => {
    const { hasConsented } = useConsent();

    const [result, setResult] = useState<LookupResult>({ status: 'idle', message: '' });
    const [manualCode, setManualCode] = useState<string>('');
    const [cameras, setCameras] = useState<CameraDevice[]>([]);
    const [selectedCameraId, setSelectedCameraId] = useState<string | undefined>();
    const [cameraError, setCameraError] = useState<string | null>(null);
    const [isScannerReady, setIsScannerReady] = useState(false);
    const [scannerOn, setScannerOn] = useState(true);

    const scannerRef = useRef<Html5Qrcode | null>(null);
    const isPausedRef = useRef(false);

    const readerId = "qr-info-reader";

    const lookupByCode = useCallback(async (code: string) => {
        if (!code) return;
        isPausedRef.current = true;
        setResult({ status: 'loading', message: '' });
        try {
            const response = await api.get<ApiResponse<QrCode>>(`/api/qrcodes/getByCode/${encodeURIComponent(code)}`);
            if (response.data.status && response.data.data) {
                setResult({ status: 'found', message: '', qrCode: response.data.data });
            } else {
                setResult({ status: 'notfound', message: 'QR Code non trovato' });
            }
        } catch (err: any) {
            if (err.response?.status === 404 || err.response?.status === 500) {
                setResult({ status: 'notfound', message: 'QR Code non trovato' });
            } else {
                setResult({
                    status: 'error',
                    message: err.response?.data?.message || err.message || 'Errore durante la ricerca',
                });
            }
        } finally {
            setTimeout(() => { isPausedRef.current = false; }, 1500);
        }
    }, []);

    const onScanSuccess = useCallback((decodedText: string) => {
        if (isPausedRef.current) return;
        if (decodedText.toLowerCase().startsWith('http:') || decodedText.toLowerCase().startsWith('https:')) return;
        lookupByCode(decodedText);
    }, [lookupByCode]);

    const onScanFailure = useCallback(() => {}, []);

    // Hardware init
    useEffect(() => {
        if (!hasConsented || !scannerOn) return;

        scannerRef.current = new Html5Qrcode(readerId, { verbose: false });
        Html5Qrcode.getCameras()
            .then(devices => { if (devices?.length) setCameras(devices); })
            .catch(err => console.error("Impossibile enumerare le fotocamere:", err));

        setIsScannerReady(true);

        return () => {
            const scanner = scannerRef.current;
            if (scanner?.isScanning) {
                scanner.stop().catch(err => console.error("Errore stop scanner:", err));
            }
            setIsScannerReady(false);
        };
    }, [hasConsented, scannerOn]);

    // Camera start
    useEffect(() => {
        if (!isScannerReady || !hasConsented || !scannerOn) return;
        const scanner = scannerRef.current;
        if (!scanner) return;

        const stopScanner = () => scanner.isScanning ? scanner.stop() : Promise.resolve();

        stopScanner().catch(() => {}).finally(() => {
            setCameraError(null);
            const config = { fps: 30 };

            if (selectedCameraId) {
                scanner.start(selectedCameraId, config, onScanSuccess, onScanFailure)
                    .catch(() => setCameraError("Impossibile avviare la fotocamera selezionata."));
            } else {
                scanner.start({ facingMode: 'environment' }, config, onScanSuccess, onScanFailure)
                    .catch(() => {
                        if (cameras.length > 0) {
                            scanner.start(cameras[0].id, config, onScanSuccess, onScanFailure)
                                .catch(() => setCameraError("Errore avvio fotocamera."));
                        } else {
                            setCameraError("Nessuna fotocamera trovata.");
                        }
                    });
            }
        });
    }, [selectedCameraId, hasConsented, cameras, isScannerReady, scannerOn, onScanSuccess, onScanFailure]);

    // Keyboard scanner support (USB readers)
    useEffect(() => {
        let buffer = '';
        let lastTime = 0;
        const handleKeyDown = (e: KeyboardEvent) => {
            if (isPausedRef.current) return;
            const target = e.target as HTMLElement;
            if (target && (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')) return;
            const now = Date.now();
            if (now - lastTime > 280) buffer = '';
            lastTime = now;
            if (e.key === 'Enter') {
                if (buffer.length > 3) lookupByCode(buffer);
                buffer = '';
            } else if (e.key.length === 1) {
                buffer += e.key;
            }
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [lookupByCode]);

    const handleManualSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const c = manualCode.trim();
        if (c) lookupByCode(c);
    };

    const handleReset = () => {
        setResult({ status: 'idle', message: '' });
        setManualCode('');
    };

    if (!hasConsented) return <ConsentBlockedView />;

    return (
        <div className="space-y-6 animate-fade-in max-w-5xl mx-auto pb-12">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-4">
                <div>
                    <h1 className="text-4xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-white to-slate-400">
                        Info QR Code
                    </h1>
                    <p className="text-slate-400 mt-2 text-lg">
                        Scansiona o inserisci un codice per vedere i dettagli. Nessuna scansione verrà registrata.
                    </p>
                </div>
                <button
                    onClick={() => setScannerOn(s => !s)}
                    className={`px-4 py-2 rounded-xl border flex items-center gap-2 transition-all ${
                        scannerOn
                            ? 'bg-sky-600 border-sky-500 text-white shadow-lg shadow-sky-500/20'
                            : 'bg-slate-800 border-slate-700 text-slate-300 hover:bg-slate-700'
                    }`}
                >
                    {Icons.Camera}
                    <span>{scannerOn ? 'Fotocamera attiva' : 'Attiva fotocamera'}</span>
                </button>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left: Scanner + manual input */}
                <div className="space-y-6">
                    {scannerOn && (
                        <>
                            {cameras.length > 1 && (
                                <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-4">
                                    <label className="text-sm font-medium text-slate-300 flex items-center gap-2 mb-2">
                                        {Icons.Camera} Fotocamera
                                    </label>
                                    <select
                                        className="w-full bg-slate-900/50 border border-slate-700 text-white text-sm rounded-xl focus:ring-2 focus:ring-sky-500 block p-2.5"
                                        value={selectedCameraId || ''}
                                        onChange={(e) => setSelectedCameraId(e.target.value)}
                                    >
                                        <option value="">Default (Posteriore)</option>
                                        {cameras.map(camera => (
                                            <option key={camera.id} value={camera.id}>
                                                {camera.label || `Camera ${camera.id.slice(0, 5)}...`}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}

                            <div className="relative overflow-hidden rounded-3xl bg-black border-4 border-slate-800 shadow-2xl aspect-square sm:aspect-[4/3]">
                                {cameraError ? (
                                    <div className="absolute inset-0 flex flex-col items-center justify-center text-rose-400 p-6 text-center">
                                        <p className="font-semibold">{cameraError}</p>
                                    </div>
                                ) : (
                                    <div id={readerId} className="w-full h-full object-cover" />
                                )}
                                <div className="absolute top-4 left-4 w-8 h-8 border-t-4 border-l-4 border-white/20 rounded-tl-lg pointer-events-none"></div>
                                <div className="absolute top-4 right-4 w-8 h-8 border-t-4 border-r-4 border-white/20 rounded-tr-lg pointer-events-none"></div>
                                <div className="absolute bottom-4 left-4 w-8 h-8 border-b-4 border-l-4 border-white/20 rounded-bl-lg pointer-events-none"></div>
                                <div className="absolute bottom-4 right-4 w-8 h-8 border-b-4 border-r-4 border-white/20 rounded-br-lg pointer-events-none"></div>
                            </div>
                        </>
                    )}

                    <form onSubmit={handleManualSubmit} className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-4 space-y-3">
                        <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                            {Icons.Search} Cerca per codice
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="text"
                                value={manualCode}
                                onChange={(e) => setManualCode(e.target.value)}
                                placeholder="Es. code_..."
                                className="flex-grow p-3 bg-slate-900/50 border border-slate-700 text-white text-sm rounded-xl focus:ring-2 focus:ring-sky-500 focus:outline-none"
                            />
                            <button
                                type="submit"
                                disabled={!manualCode.trim() || result.status === 'loading'}
                                className="bg-sky-600 hover:bg-sky-700 text-white font-bold py-3 px-6 rounded-xl transition-colors disabled:bg-slate-600 disabled:cursor-not-allowed"
                            >
                                Cerca
                            </button>
                        </div>
                    </form>
                </div>

                {/* Right: Result */}
                <div className="space-y-4">
                    {result.status === 'idle' && (
                        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8 text-center text-slate-400 flex flex-col items-center justify-center min-h-[300px]">
                            <div className="opacity-50 mb-4">{Icons.Info}</div>
                            <p>In attesa di un codice da analizzare...</p>
                        </div>
                    )}
                    {result.status === 'loading' && (
                        <div className="bg-slate-800/40 border border-slate-700/50 rounded-2xl p-8 text-center text-slate-300 min-h-[300px] flex items-center justify-center">
                            Ricerca in corso...
                        </div>
                    )}
                    {result.status === 'notfound' && (
                        <div className="bg-slate-800/40 border border-rose-500/40 rounded-2xl p-8 text-center text-rose-300 flex flex-col items-center justify-center min-h-[300px]">
                            <div className="mb-4">{Icons.NotFound}</div>
                            <p className="text-xl font-bold">QR Code non trovato</p>
                            <p className="text-sm text-slate-400 mt-2">Il codice non esiste o non hai i permessi per visualizzarlo.</p>
                        </div>
                    )}
                    {result.status === 'error' && (
                        <div className="bg-slate-800/40 border border-rose-500/40 rounded-2xl p-8 text-center text-rose-300 min-h-[300px] flex flex-col items-center justify-center">
                            <p className="text-xl font-bold">Errore</p>
                            <p className="text-sm text-slate-400 mt-2">{result.message}</p>
                        </div>
                    )}
                    {result.status === 'found' && result.qrCode && (
                        <QrInfoCard qr={result.qrCode} />
                    )}

                    {result.status !== 'idle' && result.status !== 'loading' && (
                        <button
                            onClick={handleReset}
                            className="w-full py-3 rounded-xl font-bold text-white bg-slate-700 hover:bg-slate-600 border border-slate-600 transition-all flex items-center justify-center gap-2"
                        >
                            {Icons.Refresh}
                            Nuova ricerca
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default QrInfoLookupPage;
